import type { MasterTimeOffPreview, MasterTimeOffReason, MasterTimeOffRequestView } from "@planerius/contracts/cabinet"

import { countOf } from "../../lib/plural"

/**
 * «Отпроситься» (ТЗ 05, 5-М6, вариант А) — правила без вёрстки.
 *
 * ЦЕНА ПРОСЬБЫ — ДО ОТПРАВКИ: «на эти дни записаны 4 клиента — их перенесёт или отменит
 * администратор». Мастер видит, что просит; кнопка не прячет последствия за отправкой.
 * Статусов «согласовано / отклонено» нет — их негде хранить; есть «ждёт администратора»,
 * «оформлено» (в неделе появилось отсутствие) и «отозвано».
 */

export const REASONS: readonly { readonly id: MasterTimeOffReason; readonly label: string }[] = [
  { id: "vacation", label: "Отпуск" },
  { id: "sick", label: "Больничный" },
  { id: "day_off", label: "Отгул" },
]

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"]
const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

/** «6 октября». */
export function dayWords(date: string): string {
  const [, month, day] = date.split("-").map(Number)
  return `${day ?? ""} ${MONTHS[(month ?? 1) - 1] ?? ""}`
}

/** «6–12 октября», «30 сентября – 2 октября», «6 октября». */
export function periodWords(from: string, to: string): string {
  if (from === to) return dayWords(from)
  const [, fm, fd] = from.split("-").map(Number)
  const [, tm] = to.split("-").map(Number)
  return fm === tm ? `${fd ?? ""}–${dayWords(to)}` : `${dayWords(from)} – ${dayWords(to)}`
}

/** Что не так с периодом — до сервера, теми же словами. null — отправлять можно. */
export function periodProblem(from: string, to: string, today: string): string | null {
  if (from === "" || to === "") return "Укажите, с какого и по какое число."
  if (from > to) return "Дата «с» позже даты «по»."
  if (from < today) return "Задним числом отсутствие оформляет администратор — скажите ему, он отметит."
  return null
}

/** Строка цены просьбы: смены и записанные клиенты. */
export function priceText(preview: MasterTimeOffPreview): { readonly main: string; readonly clients: string | null } {
  const shifts = countOf(preview.shifts, "смена", "смены", "смен")
  if (preview.appointments === 0) {
    return { main: `В этот период у вас ${shifts}. Записей на эти дни нет.`, clients: null }
  }
  const verb = preview.appointments % 10 === 1 && preview.appointments % 100 !== 11 ? "записан" : "записаны"
  return {
    main: `В этот период у вас ${shifts}.`,
    clients: `На эти дни ${verb} ${countOf(preview.appointments, "клиент", "клиента", "клиентов")}`,
  }
}

/** «Пн 6 · 2 записи · Ср 8 · 1». */
export function daysLine(preview: MasterTimeOffPreview): string {
  return preview.days
    .map((day, index) => {
      const at = new Date(`${day.date}T12:00:00Z`)
      const head = `${WEEKDAYS[at.getUTCDay()]} ${at.getUTCDate()}`
      return index === 0 ? `${head} · ${countOf(day.appointments, "запись", "записи", "записей")}` : `${head} · ${day.appointments}`
    })
    .join(" · ")
}

export const STATE_TEXT: Readonly<Record<MasterTimeOffRequestView["state"], string>> = {
  sent: "отправлено, ждёт администратора",
  done: "администратор закрыл дни",
  recalled: "отозвано",
}

export const STATE_TONE: Readonly<Record<MasterTimeOffRequestView["state"], "warn" | "ok" | "gray">> = {
  sent: "warn",
  done: "ok",
  recalled: "gray",
}
